import { Component, OnInit, OnDestroy, signal, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MeetingsService } from '../meetings.service';
import { SwalUtils } from '../../../../shared/utils/swal.utils';

import { isAllowedImage, readImagePreview, IMAGE_FORMAT_ERROR } from '../../../../shared/utils/image-upload';
export interface MeetingForm {
  meeting_id?: number;
  meeting_title: string;
  meeting_compte_rendu: string;
  meeting_participants: string;
  meeting_date: string;
  meeting_lieu: string;
  meeting_category: string;
  meeting_status: 'ACTI' | 'INAC';
}

@Component({
  selector: 'app-meetings-form',
  standalone: false,
  templateUrl: './meetings-form.component.html',
  styleUrls: ['./meetings-form.component.scss']
})
export class MeetingsFormComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('visualEditor') visualEditor!: ElementRef<HTMLDivElement>;

  meetingId = signal<number | null>(null);
  isLoading = signal(false);
  isFetching = signal(false);
  errorMessage = signal<string | null>(null);
  imagePreview = signal<string | null>(null);
  currentImageFile = signal<File | null>(null);
  editorMode = signal<'visual' | 'code'>('visual');

  private needsVisualSync = false;

  formData = signal<MeetingForm>({
    meeting_title: '',
    meeting_compte_rendu: '',
    meeting_participants: '',
    meeting_date: '',
    meeting_lieu: '',
    meeting_category: '',
    meeting_status: 'ACTI'
  });

  private destroy$ = new Subject<void>();

  constructor(
    private meetingsService: MeetingsService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.meetingId.set(Number(idParam));
      this.loadMeeting(Number(idParam));
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewChecked(): void {
    if (this.needsVisualSync && this.visualEditor?.nativeElement) {
      const el = this.visualEditor.nativeElement;
      if (el.innerHTML !== this.formData().meeting_compte_rendu) {
        el.innerHTML = this.formData().meeting_compte_rendu || '';
      }
      this.needsVisualSync = false;
    }
  }

  setEditorMode(mode: 'visual' | 'code'): void {
    if (mode === this.editorMode()) return;
    if (this.editorMode() === 'visual' && this.visualEditor?.nativeElement) {
      this.formData.set({ ...this.formData(), meeting_compte_rendu: this.visualEditor.nativeElement.innerHTML });
    }
    this.editorMode.set(mode);
    if (mode === 'visual') this.needsVisualSync = true;
  }

  onVisualInput(): void {
    if (this.visualEditor?.nativeElement) {
      this.formData.set({ ...this.formData(), meeting_compte_rendu: this.visualEditor.nativeElement.innerHTML });
    }
  }

  onCodeInput(event: Event): void {
    this.formData.set({ ...this.formData(), meeting_compte_rendu: (event.target as HTMLTextAreaElement).value });
  }

  execFormat(command: string, value: string = ''): void {
    this.visualEditor?.nativeElement?.focus();
    document.execCommand(command, false, value || undefined);
    this.onVisualInput();
  }

  insertHeading(level: 'h2' | 'h3'): void { this.execFormat('formatBlock', level); }
  insertParagraph(): void { this.execFormat('formatBlock', 'p'); }
  insertLink(): void {
    const url = prompt('URL du lien :');
    if (url) this.execFormat('createLink', url);
  }

  private loadMeeting(id: number): void {
    this.isFetching.set(true);
    this.errorMessage.set(null);

    this.meetingsService.getMeetingById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const m = response.data || response;
          this.formData.set({
            meeting_id: m.meeting_id,
            meeting_title: m.meeting_title || '',
            meeting_compte_rendu: m.meeting_compte_rendu || '',
            meeting_participants: m.meeting_participants || '',
            meeting_date: this.formatDateForInput(m.meeting_date),
            meeting_lieu: m.meeting_lieu || '',
            meeting_category: m.meeting_category || '',
            meeting_status: m.meeting_status || 'ACTI'
          });
          if (m.photo) this.imagePreview.set(m.photo);
          this.needsVisualSync = true;
          this.isFetching.set(false);
        },
        error: (err) => {
          this.isFetching.set(false);
          SwalUtils.error('Erreur', err?.error?.message || 'Impossible de charger cette réunion.');
        }
      });
  }

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    if (!isAllowedImage(file)) { this.errorMessage.set(IMAGE_FORMAT_ERROR); return; }
    if (file.size > 10 * 1024 * 1024) { this.errorMessage.set('Fichier trop volumineux (max 10 Mo)'); return; }
    this.currentImageFile.set(file);
    readImagePreview(file, (url) => this.imagePreview.set(url));
  }

  imageRemoved = signal(false);

  removeImage(): void {
    this.imagePreview.set(null);
    this.currentImageFile.set(null);
    this.imageRemoved.set(true);
  }

  async onSave(): Promise<void> {
    if (this.editorMode() === 'visual' && this.visualEditor?.nativeElement) {
      this.formData.set({ ...this.formData(), meeting_compte_rendu: this.visualEditor.nativeElement.innerHTML });
    }
    const data = this.formData();
    if (!data.meeting_title.trim()) { this.errorMessage.set('Le titre est requis'); return; }
    if (!data.meeting_date) { this.errorMessage.set('La date est requise'); return; }

    const confirmed = await SwalUtils.confirmGreen(
      this.isEditMode ? 'Modifier la réunion' : 'Créer la réunion',
      this.isEditMode
        ? 'Voulez-vous enregistrer les modifications de cette réunion ?'
        : 'Voulez-vous créer cette réunion ?',
      this.isEditMode ? 'Modifier' : 'Créer'
    );
    if (!confirmed.isConfirmed) return;

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const form = new FormData();
    form.append('meeting_title', data.meeting_title);
    form.append('meeting_compte_rendu', data.meeting_compte_rendu || '');
    form.append('meeting_participants', data.meeting_participants || '');
    form.append('meeting_date', data.meeting_date);
    form.append('meeting_lieu', data.meeting_lieu || '');
    form.append('meeting_category', data.meeting_category || '');
    form.append('meeting_status', data.meeting_status);
    if (this.currentImageFile()) form.append('photo', this.currentImageFile()!);
    else if (this.isEditMode && this.imageRemoved()) form.append('remove_image', '1');

    const isUpdate = this.meetingId() !== null;
    const req$ = isUpdate
      ? this.meetingsService.updateMeeting(this.meetingId()!, form)
      : this.meetingsService.createMeeting(form);

    req$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        const id = isUpdate ? this.meetingId()! : res?.data?.meeting_id;
        SwalUtils.success(
          isUpdate ? 'Réunion modifiée' : 'Réunion créée',
          isUpdate ? 'Les modifications ont été enregistrées.' : 'La réunion a été créée avec succès.'
        ).then(() => {
          if (id) {
            this.router.navigate(['/federation/reunions', id]);
          } else {
            this.router.navigate(['/federation/reunions']);
          }
        });
      },
      error: (err) => {
        this.isLoading.set(false);
        SwalUtils.error(
          'Erreur',
          err?.error?.message || 'Une erreur est survenue lors de l\'enregistrement.'
        );
      }
    });
  }

  onCancel(): void {
    const id = this.meetingId();
    if (id) {
      this.router.navigate(['/federation/reunions', id]);
    } else {
      this.router.navigate(['/federation/reunions']);
    }
  }

  updateField(field: keyof MeetingForm, value: string): void {
    this.formData.set({ ...this.formData(), [field]: value });
  }

  private formatDateForInput(dateStr: string): string {
    if (!dateStr) return '';
    try { return new Date(dateStr).toISOString().split('T')[0]; }
    catch { return dateStr; }
  }

  get isEditMode(): boolean { return this.meetingId() !== null; }
  get pageTitle(): string { return this.isEditMode ? 'Modifier la réunion' : 'Créer une réunion'; }
}
